import { DEFAULT_THEME } from "./theme.js";
import { DbScale } from "./scales/DbScale.js";
import { FrequencyScale } from "./scales/FrequencyScale.js";
import { frequencyToFraction } from "../spectrogram/frequencyMapping.js";

/** Pixel size out of a CSS font shorthand ("bold 11px sans-serif" -> 11). */
function fontSizePx(font) {
  const match = /(\d+(?:\.\d+)?)px/.exec(font || "");
  return match ? Number(match[1]) : 10;
}

export class ViewportRenderer {
  constructor(canvasElement, timeline, daw, theme = {}) {
    this.canvas = canvasElement;
    this.ctx = this.canvas.getContext("2d");
    this.timeline = timeline;
    this.daw = daw;
    this.renderScheduled = false;
    this.minTrackHeight = 80;
    this.trackSpacing = 10;
    this.bottomPadding = 40;
    this.rulerHeight = 60;
    this.verticalScrollOffset = 0;
    this.scrollbarDragState = null;
    this.horizontalScrollbarDragState = null;

    this.minimapCanvas = null;
    this.minimapCtx = null;
    this.minimapDragState = null;

    /**
     * What the mouse means over the track lanes.
     *
     * The ruler strip, scrollbars, and minimap behave the same either way; only
     * the lanes change. "arrange" grabs a track to move it in time; "annotate"
     * seeks on a click and drags out a region, exactly as the ruler does.
     */
    this.interactionMode = "arrange";

    /**
     * How non-active-but-visible region groups draw.
     *
     * "full" (default) draws every visible group's regions at full height,
     * dimmed if not active. "minimized" instead draws a thin colour bar along
     * the top edge for everything but the active group, so several groups can
     * stay visible without the lanes disappearing under overlapping boxes.
     * The active group is always drawn in full either way.
     */
    this.regionDisplayMode = "full";

    /**
     * Default for drawing a region group's name on its regions: "always",
     * "active" (only the group being edited), or "never". A group's own
     * `showName` overrides it either way. "never" so existing hosts are
     * unchanged.
     */
    this.regionGroupNameDisplay = "never";

    /**
     * How many tracks to draw, or null for all of them.
     *
     * Purely a display concern — hidden tracks still play. Swapping one track
     * for another means both exist for a moment, and without this the lane
     * splits in two and the waveform shrinks and regrows around the handover.
     * A limit of 1 keeps the outgoing track on screen until it's actually gone.
     */
    this.visibleTrackLimit = null;
    this.lastLayoutSignature = null;
    /** trackId -> { db, frequency } scale renderers attached by the host. */
    this.scales = new Map();

    // Selection drag state
    this.selectionDragState = null; // { type: 'create' | 'move' | 'resize-start' | 'resize-end', startTime, startMouseTime }
    this.regionDragState = null; // { type: 'move' | 'resize-start' | 'resize-end', regionIndex, originalRegion, startMouseTime }
    this.hoveredRegionIndex = null; // Index of region being hovered over

    // Throttle for region change callbacks during drag
    this.lastRegionChangeCallbackTime = 0;
    this.regionChangeCallbackThrottleMs = 16; // ~60fps

    // Track mipmap levels for logging changes
    this.lastMipmapLevels = new Map(); // trackId -> resolution

    // Theme configuration (merge with defaults)
    this.theme = { ...DEFAULT_THEME, ...theme };

    // Ensure scrollbar config exists and merge legacy properties
    if (!this.theme.scrollbar) {
      this.theme.scrollbar = DEFAULT_THEME.scrollbar;
    }
    this.theme.scrollbar = {
      ...DEFAULT_THEME.scrollbar,
      ...this.theme.scrollbar,
      // Support legacy properties
      width: this.theme.scrollbar.width || this.theme.scrollbarWidth,
      trackColor:
        this.theme.scrollbar.trackColor || this.theme.scrollbarTrackColor,
      thumbColor:
        this.theme.scrollbar.thumbColor || this.theme.scrollbarThumbColor,
      thumbColorActive:
        this.theme.scrollbar.thumbColorActive ||
        this.theme.scrollbarThumbColorActive,
      thumbBorder:
        this.theme.scrollbar.thumbBorder || this.theme.scrollbarThumbBorder,
    };

    // The renderer dereferences theme.selectionMenu unconditionally, so a theme
    // that omits it — or nulls it — must not be able to take the context menu
    // down with it.
    this.theme.selectionMenu = {
      ...DEFAULT_THEME.selectionMenu,
      ...(this.theme.selectionMenu || {}),
    };

    // Same protection for the rulers: a host supplying only a text colour must
    // not lose the tick lengths and spacing rules along with it.
    this.theme.scales = {
      ...DEFAULT_THEME.scales,
      ...(this.theme.scales || {}),
    };

    // Keep legacy property for backward compatibility
    this.scrollbarWidth = this.theme.scrollbar.width;

    this.setupCanvas();
    this.setupEventListeners();
  }

  /**
   * Update theme and re-render
   */
  setTheme(newTheme) {
    this.theme = { ...DEFAULT_THEME, ...newTheme };

    // Ensure scrollbar config exists and merge legacy properties
    if (!this.theme.scrollbar) {
      this.theme.scrollbar = DEFAULT_THEME.scrollbar;
    }
    this.theme.scrollbar = {
      ...DEFAULT_THEME.scrollbar,
      ...this.theme.scrollbar,
      // Support legacy properties
      width: this.theme.scrollbar.width || this.theme.scrollbarWidth,
      trackColor:
        this.theme.scrollbar.trackColor || this.theme.scrollbarTrackColor,
      thumbColor:
        this.theme.scrollbar.thumbColor || this.theme.scrollbarThumbColor,
      thumbColorActive:
        this.theme.scrollbar.thumbColorActive ||
        this.theme.scrollbarThumbColorActive,
      thumbBorder:
        this.theme.scrollbar.thumbBorder || this.theme.scrollbarThumbBorder,
    };

    // The renderer dereferences theme.selectionMenu unconditionally, so a theme
    // that omits it — or nulls it — must not be able to take the context menu
    // down with it.
    this.theme.selectionMenu = {
      ...DEFAULT_THEME.selectionMenu,
      ...(this.theme.selectionMenu || {}),
    };

    // Same protection for the rulers: a host supplying only a text colour must
    // not lose the tick lengths and spacing rules along with it.
    this.theme.scales = {
      ...DEFAULT_THEME.scales,
      ...(this.theme.scales || {}),
    };

    for (const pair of this.scales.values()) {
      pair.db?.setTheme(this.theme.scales);
      pair.frequency?.setTheme(this.theme.scales);
    }

    // Keep legacy property for backward compatibility
    this.scrollbarWidth = this.theme.scrollbar.width;
    this.scheduleRender();
  }

  setMinimapCanvas(minimapCanvas) {
    this.minimapCanvas = minimapCanvas;
    this.minimapCtx = minimapCanvas ? minimapCanvas.getContext("2d") : null;
    if (this.minimapCanvas) {
      this.setupMinimapEventListeners();
      // Trigger resize to properly size the minimap canvas
      this.resizeCanvas();
    }
  }

  setupCanvas() {
    this.resizeCanvas();
    window.addEventListener("resize", () => this.resizeCanvas());
  }

  resizeCanvas() {
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = this.canvas.offsetWidth * dpr;
    this.canvas.height = this.canvas.offsetHeight * dpr;
    this.ctx.scale(dpr, dpr);

    if (this.minimapCanvas) {
      this.minimapCanvas.width = this.minimapCanvas.offsetWidth * dpr;
      this.minimapCanvas.height = this.minimapCanvas.offsetHeight * dpr;
      this.minimapCtx.scale(dpr, dpr);
    }

    this.timeline.updateViewport(this.canvas.offsetWidth);
    this.scheduleRender();
  }

  /** The tracks that are actually drawn. Hidden ones still play. */
  getVisibleTracks() {
    const tracks = this.timeline.tracks;
    return this.visibleTrackLimit === null ? tracks : tracks.slice(0, this.visibleTrackLimit);
  }

  /**
   * Where each drawn track lane sits, in CSS pixels on the main canvas.
   *
   * The host needs this to put anything alongside a lane — a dB or frequency
   * scale on its own canvas, a per-track control strip, a label column. None of
   * that can live inside the canvas, so the geometry has to come out.
   *
   * **The arithmetic here is the same arithmetic `drawTracks` uses**, and has to
   * stay that way: if they diverge, a scale drifts away from the lane it
   * describes as you scroll, which looks like a rendering bug anywhere but here.
   *
   * `visible` uses the same test as the draw loop, so a lane scrolled out of
   * view reports false rather than a y-coordinate off the canvas.
   */
  trackLayout() {
    const trackHeight = this.calculateTrackHeight();
    const viewportHeight = this.canvas.offsetHeight - this.rulerHeight;
    const width = this.canvas.offsetWidth;

    return this.getVisibleTracks().map((track, index) => {
      const y = 20 + index * (trackHeight + this.trackSpacing) - this.verticalScrollOffset;

      return {
        id: track.id,
        x: 0,
        width,
        y,
        height: trackHeight,
        visible: y + trackHeight >= 0 && y <= viewportHeight,
      };
    });
  }

  /**
   * Tell the host when that geometry moves.
   *
   * Signature-compared rather than fired every frame: this runs inside the
   * render loop, and a host that repositions DOM on every callback would be
   * doing layout 60 times a second for a picture that mostly hasn't moved.
   */
  emitLayoutChange() {
    const layout = this.trackLayout();
    const signature = layout.map((l) => `${l.id}:${l.y}:${l.height}:${l.visible}`).join("|");

    if (signature === this.lastLayoutSignature) return;
    this.lastLayoutSignature = signature;

    this.daw?.onTrackLayoutChange?.(layout);
    this.drawScales();
  }

  // ---------------------------------------------------------- scales ---

  /**
   * The band a track's spectrogram is showing.
   *
   * Defaults to the whole analysed range, which starts at the server's
   * `minFrequency` — 20 Hz, not 0. The ruler reads the same value, so the two
   * agree by construction.
   */
  frequencyRangeFor(track) {
    if (!track?.spectrogram) return null;
    return (
      track.frequencyRange || {
        min: track.spectrogram.minFrequency,
        max: track.spectrogram.maxFrequency,
      }
    );
  }

  /**
   * Attach a ruler to a canvas the host owns.
   *
   * @param {string} trackId
   * @param {"db"|"frequency"} kind
   * @param {HTMLCanvasElement} canvas
   * @param {Object} [options] - `side`, and per-instance theme overrides
   */
  attachScale(trackId, kind, canvas, options = {}) {
    const pair = this.scales.get(trackId) || {};
    pair[kind]?.destroy();

    const theme = { ...this.theme.scales, ...(options.theme || {}) };
    const Scale = kind === "db" ? DbScale : FrequencyScale;

    pair[kind] = new Scale(canvas, { ...options, theme });
    this.scales.set(trackId, pair);

    this.drawScales();
    return pair[kind];
  }

  detachScale(trackId, kind) {
    const pair = this.scales.get(trackId);
    if (!pair?.[kind]) return;

    pair[kind].destroy();
    delete pair[kind];
    if (!pair.db && !pair.frequency) this.scales.delete(trackId);
  }

  detachScales(trackId) {
    this.detachScale(trackId, "db");
    this.detachScale(trackId, "frequency");
  }

  /**
   * Redraw every attached ruler.
   *
   * Driven by layout changes and explicit invalidation rather than by the frame
   * loop: a ruler only moves when the lane resizes, the band changes or the
   * theme does, and re-rendering text sixty times a second for a picture that
   * hasn't moved is waste the host would feel.
   */
  drawScales() {
    if (this.scales.size === 0) return;

    for (const [trackId, pair] of this.scales) {
      const track = this.timeline.tracks.find((t) => t.id === trackId);

      if (pair.frequency) {
        const range = this.frequencyRangeFor(track);
        pair.frequency.setHighlights(track?.frequencyHighlights);
        if (range) pair.frequency.setRange(range, track.spectrogram.binScale);
        else pair.frequency.draw();
      }

      pair.db?.draw();
    }
  }


  calculateTrackHeight() {
    const numTracks = this.getVisibleTracks().length;
    if (numTracks === 0) return this.minTrackHeight;

    const topPadding = 20;
    const bottomPadding = 20;
    const availableHeight =
      this.canvas.offsetHeight - this.rulerHeight - topPadding - bottomPadding;
    const totalSpacing = (numTracks - 1) * this.trackSpacing;
    const heightPerTrack = (availableHeight - totalSpacing) / numTracks;

    return Math.max(this.minTrackHeight, heightPerTrack);
  }

  getTotalContentHeight() {
    const numTracks = this.getVisibleTracks().length;
    if (numTracks === 0) return 0;

    const topPadding = 20;
    const bottomPadding = 20; // Match top padding for symmetry
    const trackHeight = this.calculateTrackHeight();

    // Total height = top padding + all tracks + spacing between tracks + bottom padding
    return (
      topPadding +
      numTracks * trackHeight +
      (numTracks - 1) * this.trackSpacing +
      bottomPadding
    );
  }

  getMaxVerticalScroll() {
    const contentHeight = this.getTotalContentHeight();
    const viewportHeight = this.canvas.offsetHeight - this.rulerHeight;
    return Math.max(0, contentHeight - viewportHeight);
  }

  getMaxHorizontalScroll() {
    const totalDuration = this.timeline.getEffectiveDuration(this.canvas.offsetWidth);
    const visibleDuration = this.timeline.getVisibleDuration();
    return Math.max(0, totalDuration - visibleDuration);
  }

  setupEventListeners() {
    this.canvas.addEventListener("wheel", (e) => this.handleWheel(e), {
      passive: false,
    });
    this.canvas.addEventListener("mousedown", (e) => this.handleMouseDown(e));
    this.canvas.addEventListener("mousemove", (e) => this.handleMouseMove(e));
    this.canvas.addEventListener("mouseup", (e) => this.handleMouseUp(e));
    this.canvas.addEventListener("dblclick", (e) => this.handleDoubleClick(e));
    this.canvas.addEventListener("contextmenu", (e) =>
      this.handleContextMenu(e)
    );

    // Keyboard events for selection
    window.addEventListener("keydown", (e) => this.handleKeyDown(e));
  }

  setupMinimapEventListeners() {
    this.minimapCanvas.addEventListener("mousedown", (e) =>
      this.handleMinimapMouseDown(e)
    );
    this.minimapCanvas.addEventListener("mousemove", (e) =>
      this.handleMinimapMouseMove(e)
    );
    this.minimapCanvas.addEventListener("mouseup", (e) =>
      this.handleMinimapMouseUp(e)
    );
    this.minimapCanvas.addEventListener("mouseleave", () => {
      if (this.minimapDragState) {
        this.minimapDragState = null;
        this.minimapCanvas.style.cursor = "pointer";
      }
    });
  }

  handleWheel(e) {
    e.preventDefault();

    if (e.shiftKey) {
      const scrollAmount = e.deltaY;
      this.verticalScrollOffset += scrollAmount;
      const maxScroll = this.getMaxVerticalScroll();
      this.verticalScrollOffset = Math.max(
        0,
        Math.min(maxScroll, this.verticalScrollOffset)
      );
      this.scheduleRender();
    } else if (e.ctrlKey) {
      this.zoomAtPoint(e.offsetX, e.deltaY > 0 ? 0.9 : 1.1);
    } else {
      const scrollAmount = e.deltaY * 0.01;
      this.timeline.scroll(scrollAmount, this.canvas.offsetWidth);
      this.timeline.updateViewport(this.canvas.offsetWidth);
      this.scheduleRender();
    }
  }

  zoomAtPoint(canvasX, zoomFactor) {
    const timeAtCursor = this.timeline.canvasXToTime(canvasX);

    this.timeline.pixelsPerSecond *= zoomFactor;
    this.timeline.pixelsPerSecond = Math.min(
      500,
      Math.max(0.1, this.timeline.pixelsPerSecond)
    );

    const newCanvasWidth = this.canvas.offsetWidth;
    this.timeline.updateViewport(newCanvasWidth);

    const newTimeAtCursor = this.timeline.canvasXToTime(canvasX);
    this.timeline.viewportScrollTime += timeAtCursor - newTimeAtCursor;

    const effectiveDuration = this.timeline.getEffectiveDuration(
      this.canvas.offsetWidth
    );
    const maxScroll = Math.max(
      0,
      effectiveDuration - this.timeline.getVisibleDuration()
    );
    this.timeline.viewportScrollTime = Math.max(
      0,
      Math.min(maxScroll, this.timeline.viewportScrollTime)
    );

    this.timeline.updateViewport(newCanvasWidth);
    this.scheduleRender();

    if (this.daw && this.daw.onZoomChange) {
      this.daw.onZoomChange(this.timeline.pixelsPerSecond);
    }

    if (this.daw && this.daw.onScrollChange) {
      this.daw.onScrollChange(this.timeline.viewportScrollTime);
    }
  }

  getTrackAtPoint(canvasX, canvasY) {
    const time = this.timeline.canvasXToTime(canvasX);
    const trackHeight = this.calculateTrackHeight();
    const scrolledY = canvasY + this.verticalScrollOffset;

    // Only what's drawn can be hit: a track nobody can see shouldn't be
    // grabbable either.
    const visible = this.getVisibleTracks();
    for (let i = 0; i < visible.length; i++) {
      const track = visible[i];
      const trackY = 20 + i * (trackHeight + this.trackSpacing);

      if (scrolledY >= trackY && scrolledY <= trackY + trackHeight) {
        const trackStartTime = track.timeOffset;
        const trackEndTime = track.timeOffset + track.duration;

        if (time >= trackStartTime && time <= trackEndTime) {
          return { track, index: i, trackY };
        }
      }
    }

    return null;
  }

  getTimeAtPoint(canvasX) {
    return this.timeline.canvasXToTime(canvasX);
  }

  getSelectionHandleAtPoint(canvasX, canvasY) {
    if (!this.timeline.selection) return null;

    const rulerY = this.canvas.offsetHeight - this.rulerHeight;
    if (canvasY < rulerY) return null; // Only detect handles in ruler area

    const startX = this.timeline.timeToCanvasX(this.timeline.selection.start);
    const endX = this.timeline.timeToCanvasX(this.timeline.selection.end);
    const handleWidth = this.theme.selectionHandleWidth;

    if (Math.abs(canvasX - startX) <= handleWidth / 2) {
      return "start";
    }
    if (Math.abs(canvasX - endX) <= handleWidth / 2) {
      return "end";
    }

    return null;
  }

  isPointInSelection(canvasX, canvasY) {
    if (!this.timeline.selection) return false;

    const rulerY = this.canvas.offsetHeight - this.rulerHeight;
    if (canvasY < rulerY) return false; // Only detect in ruler area

    const startX = this.timeline.timeToCanvasX(this.timeline.selection.start);
    const endX = this.timeline.timeToCanvasX(this.timeline.selection.end);

    return canvasX >= startX && canvasX <= endX;
  }

  // Region detection methods
  getRegionAtPoint(canvasX, canvasY) {
    const rulerY = this.canvas.offsetHeight - this.rulerHeight;
    if (canvasY < rulerY) return null; // Only detect regions in ruler area

    const regions = this.timeline.getActiveRegions();
    const mouseTime = this.timeline.canvasXToTime(canvasX);

    for (let i = 0; i < regions.length; i++) {
      const region = regions[i];
      if (mouseTime >= region.start && mouseTime <= region.end) {
        return { region, index: i };
      }
    }

    return null;
  }

  getRegionAtPointAnywhere(canvasX, canvasY) {
    // Check for region at any Y position (not just ruler)
    const regions = this.timeline.getActiveRegions();
    const mouseTime = this.timeline.canvasXToTime(canvasX);

    for (let i = 0; i < regions.length; i++) {
      const region = regions[i];
      if (mouseTime >= region.start && mouseTime <= region.end) {
        return { region, index: i };
      }
    }

    return null;
  }

  getRegionHandleAtPoint(canvasX, canvasY) {
    const rulerY = this.canvas.offsetHeight - this.rulerHeight;
    if (canvasY < rulerY) return null; // Only detect handles in ruler area

    const regions = this.timeline.getActiveRegions();
    const handleWidth = 8; // Handle detection width in pixels

    for (let i = 0; i < regions.length; i++) {
      const region = regions[i];
      const startX = this.timeline.timeToCanvasX(region.start);
      const endX = this.timeline.timeToCanvasX(region.end);

      if (Math.abs(canvasX - startX) <= handleWidth) {
        return { type: "start", index: i, region };
      }
      if (Math.abs(canvasX - endX) <= handleWidth) {
        return { type: "end", index: i, region };
      }
    }

    return null;
  }

  /**
   * Start dragging out a new region from `canvasX`.
   *
   * Shared by the ruler strip and, in annotate mode, the track lanes — so both
   * honour the active group's read-only and maxRegions rules identically rather
   * than growing two copies of the same checks.
   *
   * @returns {boolean} whether a create drag actually started
   */
  beginRegionCreate(canvasX, activeGroup, { seekOnClick = false } = {}) {
    if (activeGroup && activeGroup.readOnly) return false;
    if (
      activeGroup &&
      activeGroup.maxRegions !== null &&
      activeGroup.regions.length >= activeGroup.maxRegions
    ) {
      this.log(`Cannot create region: maximum of ${activeGroup.maxRegions} region(s) reached`);
      return false;
    }

    const clickTime = this.getTimeAtPoint(canvasX);
    const clickSample = this.timeline.timeToSamples(clickTime);

    this.selectionDragState = {
      type: "create",
      startTime: clickTime,
      startMouseTime: clickTime,
      seekOnClick,
    };
    this.timeline.selection = {
      start: clickTime,
      end: clickTime,
      startSample: clickSample,
      endSample: clickSample,
    };

    this.scheduleRender();
    if (this.daw && this.daw.onSelectionChange) {
      this.daw.onSelectionChange(this.timeline.selection);
    }
    return true;
  }

  /** The group regions are currently being added to, if any. */
  getActiveGroup() {
    return this.timeline.activeRegionGroupId
      ? this.timeline.regionGroups.get(this.timeline.activeRegionGroupId)
      : null;
  }

  /**
   * A region belonging to some other visible, interactable group at this X —
   * the active group is never a match here, since a click on it is handled by
   * the existing handle/move logic instead. Y isn't checked: callers gate that
   * against whatever's actually drawn for that group under the current
   * display mode (the ruler in "full" mode, the edge bar in "minimized"), so
   * a click only switches groups when it lands on something visible.
   */
  findInteractableRegionAt(canvasX) {
    const activeId = this.timeline.activeRegionGroupId;
    const mouseTime = this.timeline.canvasXToTime(canvasX);

    for (const [groupId, group] of this.timeline.getVisibleRegionGroupEntries()) {
      if (groupId === activeId || !this.timeline.isRegionGroupInteractable(group)) continue;

      for (const region of group.regions) {
        if (mouseTime >= region.start && mouseTime <= region.end) {
          return { groupId, region };
        }
      }
    }
    return null;
  }

  /** Top of the minimized-mode indicator bar, per `theme.regionGroupBarPosition`. */
  getRegionGroupBarY() {
    const barHeight = this.theme.regionGroupBarHeight;
    if (this.theme.regionGroupBarPosition === "bottom") {
      const rulerY = this.canvas.offsetHeight - this.rulerHeight;
      return rulerY - barHeight;
    }
    return 0;
  }

  /** Whether canvasY falls within the minimized-mode indicator bar. */
  isPointInRegionGroupBar(canvasY) {
    const barY = this.getRegionGroupBarY();
    return canvasY >= barY && canvasY <= barY + this.theme.regionGroupBarHeight;
  }

  handleMouseDown(e) {
    // Check vertical scrollbar
    if (this.isPointOnVerticalScrollbarThumb(e.offsetX, e.offsetY)) {
      this.scrollbarDragState = {
        isDragging: true,
        startMouseY: e.offsetY,
        startScrollOffset: this.verticalScrollOffset,
      };
      this.canvas.style.cursor = "grabbing";
      this.scheduleRender();
      return;
    }

    if (this.isPointOnVerticalScrollbarTrack(e.offsetX, e.offsetY)) {
      const geom = this.getVerticalScrollbarGeometry();
      if (e.offsetY < geom.thumbY) {
        const pageSize = this.canvas.offsetHeight - this.rulerHeight;
        this.verticalScrollOffset = Math.max(
          0,
          this.verticalScrollOffset - pageSize
        );
      } else if (e.offsetY > geom.thumbY + geom.thumbHeight) {
        const pageSize = this.canvas.offsetHeight - this.rulerHeight;
        this.verticalScrollOffset = Math.min(
          geom.maxScroll,
          this.verticalScrollOffset + pageSize
        );
      }
      this.scheduleRender();
      return;
    }

    // Check horizontal scrollbar
    if (this.isPointOnHorizontalScrollbarThumb(e.offsetX, e.offsetY)) {
      this.horizontalScrollbarDragState = {
        isDragging: true,
        startMouseX: e.offsetX,
        startScrollTime: this.timeline.viewportScrollTime,
      };
      this.canvas.style.cursor = "grabbing";
      this.scheduleRender();
      return;
    }

    if (this.isPointOnHorizontalScrollbarTrack(e.offsetX, e.offsetY)) {
      const geom = this.getHorizontalScrollbarGeometry();
      if (e.offsetX < geom.thumbX) {
        // Click before thumb - page left
        const pageSize = this.timeline.getVisibleDuration();
        this.timeline.viewportScrollTime = Math.max(
          0,
          this.timeline.viewportScrollTime - pageSize
        );
        this.timeline.updateViewport(this.canvas.offsetWidth);
      } else if (e.offsetX > geom.thumbX + geom.thumbWidth) {
        // Click after thumb - page right
        const pageSize = this.timeline.getVisibleDuration();
        this.timeline.viewportScrollTime = Math.min(
          geom.maxScroll,
          this.timeline.viewportScrollTime + pageSize
        );
        this.timeline.updateViewport(this.canvas.offsetWidth);
      }
      this.scheduleRender();
      return;
    }

    // In minimized mode, a non-active group's only visible (and clickable)
    // surface is its edge bar, wherever that falls on the canvas — outside
    // the ruler entirely, so this has to be checked before the ruler split.
    if (
      e.button === 0 &&
      this.regionDisplayMode === "minimized" &&
      this.isPointInRegionGroupBar(e.offsetY)
    ) {
      const elsewhere = this.findInteractableRegionAt(e.offsetX);
      if (elsewhere) {
        if (this.daw) this.daw.setActiveRegionGroup(elsewhere.groupId);
        return;
      }
    }

    const rulerY = this.canvas.offsetHeight - this.rulerHeight;
    const isInRuler = e.offsetY >= rulerY;

    // Check for interactions in ruler area
    if (isInRuler) {
      // Only allow left mouse button (button 0) for region interactions
      if (e.button !== 0) return;

      // Check if active region group is read-only
      const activeGroup = this.timeline.activeRegionGroupId
        ? this.timeline.regionGroups.get(this.timeline.activeRegionGroupId)
        : null;
      const isReadOnly = activeGroup && activeGroup.readOnly;

      // Check for region handle interaction first (existing regions)
      const regionHandle = this.getRegionHandleAtPoint(e.offsetX, e.offsetY);
      if (regionHandle) {
        if (!isReadOnly) {
          this.regionDragState = {
            type: regionHandle.type === "start" ? "resize-start" : "resize-end",
            regionIndex: regionHandle.index,
            originalRegion: { ...regionHandle.region },
            startMouseTime: this.getTimeAtPoint(e.offsetX),
          };
          this.canvas.style.cursor = "ew-resize";
        }
        return;
      }

      // Check if clicking in existing region to move it
      const regionAtPoint = this.getRegionAtPoint(e.offsetX, e.offsetY);
      if (regionAtPoint) {
        if (!isReadOnly) {
          this.regionDragState = {
            type: "move",
            regionIndex: regionAtPoint.index,
            originalRegion: { ...regionAtPoint.region },
            startMouseTime: this.getTimeAtPoint(e.offsetX),
          };
          this.canvas.style.cursor = "grab";
        }
        return;
      }

      // A region belonging to a different, interactable, visible group — in
      // "full" mode it's drawn right here in the ruler too, so a click on it
      // switches which group is being edited instead of starting a new
      // region underneath it. In "minimized" mode other groups aren't drawn
      // in the ruler at all, so this only applies to "full".
      if (this.regionDisplayMode === "full") {
        const elsewhere = this.findInteractableRegionAt(e.offsetX);
        if (elsewhere) {
          if (this.daw) this.daw.setActiveRegionGroup(elsewhere.groupId);
          return;
        }
      }

      // Check for temporary selection handle interaction (while creating)
      const handle = this.getSelectionHandleAtPoint(e.offsetX, e.offsetY);
      if (handle) {
        this.selectionDragState = {
          type: handle === "start" ? "resize-start" : "resize-end",
          startTime: this.timeline.selection[handle],
          startMouseTime: this.getTimeAtPoint(e.offsetX),
        };
        this.canvas.style.cursor = "ew-resize";
        return;
      }

      // Check if clicking in existing temporary selection to move it
      if (this.isPointInSelection(e.offsetX, e.offsetY)) {
        this.selectionDragState = {
          type: "move",
          startSelection: { ...this.timeline.selection },
          startMouseTime: this.getTimeAtPoint(e.offsetX),
        };
        this.canvas.style.cursor = "grabbing";
        return;
      }

      // Start creating a new region in the ruler area.
      this.beginRegionCreate(e.offsetX, activeGroup);
      return;
    }

    // Track lanes. What a press means here is the whole difference between the
    // two interaction modes.
    if (e.button !== 0) return;

    if (this.interactionMode === "annotate") {
      // Drag anywhere in a lane to mark a region; a press that doesn't move is
      // a seek, which is handled on mouse-up.
      if (this.beginRegionCreate(e.offsetX, this.getActiveGroup(), { seekOnClick: true })) return;

      // Regions can't be added — read-only group, or it's full. A press is
      // still a seek.
      this.dragState = { isDragging: false, clickTime: this.getTimeAtPoint(e.offsetX) };
      return;
    }

    const trackInfo = this.getTrackAtPoint(e.offsetX, e.offsetY);

    if (trackInfo) {
      this.dragState = {
        track: trackInfo.track,
        startOffset: trackInfo.track.timeOffset,
        startMouseTime: this.getTimeAtPoint(e.offsetX),
        startMouseX: e.offsetX,
        moved: false,
        isDragging: true,
      };
      this.canvas.style.cursor = "grabbing";
    } else {
      this.dragState = {
        isDragging: false,
        clickTime: this.getTimeAtPoint(e.offsetX),
      };
    }
  }

  handleMouseMove(e) {
    // Handle vertical scrollbar dragging
    if (this.scrollbarDragState && this.scrollbarDragState.isDragging) {
      const deltaY = e.offsetY - this.scrollbarDragState.startMouseY;
      const geom = this.getVerticalScrollbarGeometry();

      const scrollableHeight = geom.scrollbarHeight - geom.thumbHeight;
      const scrollRatio = scrollableHeight > 0 ? deltaY / scrollableHeight : 0;
      const scrollDelta = scrollRatio * geom.maxScroll;

      this.verticalScrollOffset =
        this.scrollbarDragState.startScrollOffset + scrollDelta;
      this.verticalScrollOffset = Math.max(
        0,
        Math.min(geom.maxScroll, this.verticalScrollOffset)
      );

      this.scheduleRender();
      return;
    }

    // Handle horizontal scrollbar dragging
    if (
      this.horizontalScrollbarDragState &&
      this.horizontalScrollbarDragState.isDragging
    ) {
      const deltaX = e.offsetX - this.horizontalScrollbarDragState.startMouseX;
      const geom = this.getHorizontalScrollbarGeometry();

      const scrollableWidth = geom.scrollbarWidth - geom.thumbWidth;
      const scrollRatio = scrollableWidth > 0 ? deltaX / scrollableWidth : 0;
      const scrollDelta = scrollRatio * geom.maxScroll;

      this.timeline.viewportScrollTime =
        this.horizontalScrollbarDragState.startScrollTime + scrollDelta;
      this.timeline.viewportScrollTime = Math.max(
        0,
        Math.min(geom.maxScroll, this.timeline.viewportScrollTime)
      );
      this.timeline.updateViewport(this.canvas.offsetWidth);

      this.scheduleRender();
      return;
    }

    // Handle region dragging
    if (this.regionDragState) {
      const currentMouseTime = this.getTimeAtPoint(e.offsetX);
      const regions = this.timeline.getActiveRegions();

      if (this.regionDragState.regionIndex >= regions.length) {
        this.regionDragState = null;
        return;
      }

      const region = regions[this.regionDragState.regionIndex];
      let regionChanged = false;

      if (this.regionDragState.type === "resize-start") {
        const newStart = Math.max(0, currentMouseTime);
        if (newStart < region.end) {
          region.start = newStart;
          region.startSample = this.timeline.timeToSamples(newStart);
          regionChanged = true;
          this.scheduleRender();
        }
      } else if (this.regionDragState.type === "resize-end") {
        const newEnd = Math.max(0, currentMouseTime);
        if (newEnd > region.start) {
          region.end = newEnd;
          region.endSample = this.timeline.timeToSamples(newEnd);
          regionChanged = true;
          this.scheduleRender();
        }
      } else if (this.regionDragState.type === "move") {
        const delta = currentMouseTime - this.regionDragState.startMouseTime;
        const duration =
          this.regionDragState.originalRegion.end -
          this.regionDragState.originalRegion.start;
        let newStart = this.regionDragState.originalRegion.start + delta;
        newStart = Math.max(0, newStart);
        const newEnd = newStart + duration;
        region.start = newStart;
        region.end = newEnd;
        region.startSample = this.timeline.timeToSamples(newStart);
        region.endSample = this.timeline.timeToSamples(newEnd);
        regionChanged = true;
        this.scheduleRender();
      }

      // Live updates go to onRegionsDrag, throttled; onRegionsChange waits for
      // mouseup so a host persisting regions sees one change per gesture.
      if (regionChanged) {
        this.regionDragState.changed = true;
        const now = Date.now();
        if (
          this.daw &&
          this.daw.onRegionsDrag &&
          now - this.lastRegionChangeCallbackTime >=
            this.regionChangeCallbackThrottleMs
        ) {
          const groupId = this.timeline.activeRegionGroupId;
          this.daw.onRegionsDrag(groupId, this.daw.getRegions(groupId));
          this.lastRegionChangeCallbackTime = now;
        }
      }

      return;
    }

    // Handle selection dragging
    if (this.selectionDragState) {
      const currentMouseTime = this.getTimeAtPoint(e.offsetX);

      if (this.selectionDragState.type === "create") {
        const start = Math.min(
          this.selectionDragState.startTime,
          currentMouseTime
        );
        const end = Math.max(
          this.selectionDragState.startTime,
          currentMouseTime
        );
        this.timeline.selection = {
          start,
          end,
          startSample: this.timeline.timeToSamples(start),
          endSample: this.timeline.timeToSamples(end),
        };
        this.scheduleRender();
        if (this.daw && this.daw.onSelectionChange) {
          this.daw.onSelectionChange(this.timeline.selection);
        }
      } else if (this.selectionDragState.type === "resize-start") {
        const newStart = Math.max(0, currentMouseTime);
        if (newStart < this.timeline.selection.end) {
          this.timeline.selection.start = newStart;
          this.timeline.selection.startSample =
            this.timeline.timeToSamples(newStart);
          this.scheduleRender();
          if (this.daw && this.daw.onSelectionChange) {
            this.daw.onSelectionChange(this.timeline.selection);
          }
        }
      } else if (this.selectionDragState.type === "resize-end") {
        const newEnd = Math.max(0, currentMouseTime);
        if (newEnd > this.timeline.selection.start) {
          this.timeline.selection.end = newEnd;
          this.timeline.selection.endSample =
            this.timeline.timeToSamples(newEnd);
          this.scheduleRender();
          if (this.daw && this.daw.onSelectionChange) {
            this.daw.onSelectionChange(this.timeline.selection);
          }
        }
      } else if (this.selectionDragState.type === "move") {
        const delta = currentMouseTime - this.selectionDragState.startMouseTime;
        const duration =
          this.selectionDragState.startSelection.end -
          this.selectionDragState.startSelection.start;
        let newStart = this.selectionDragState.startSelection.start + delta;
        newStart = Math.max(0, newStart);
        const newEnd = newStart + duration;
        this.timeline.selection = {
          start: newStart,
          end: newEnd,
          startSample: this.timeline.timeToSamples(newStart),
          endSample: this.timeline.timeToSamples(newEnd),
        };
        this.scheduleRender();
        if (this.daw && this.daw.onSelectionChange) {
          this.daw.onSelectionChange(this.timeline.selection);
        }
      }
      return;
    }

    if (this.dragState && this.dragState.isDragging) {
      // A press that never travels is a click, not a drag. Without this a plain
      // click on a track reports an offset change to the same value it already
      // had, and never seeks.
      if (Math.abs(e.offsetX - this.dragState.startMouseX) > 2) this.dragState.moved = true;

      const currentMouseTime = this.getTimeAtPoint(e.offsetX);
      const delta = currentMouseTime - this.dragState.startMouseTime;

      const newTimeOffset = Math.max(0, this.dragState.startOffset + delta);
      this.dragState.track.setTimeOffset(newTimeOffset);

      this.scheduleRender();
    } else {
      if (
        this.isPointOnVerticalScrollbarThumb(e.offsetX, e.offsetY) ||
        this.isPointOnHorizontalScrollbarThumb(e.offsetX, e.offsetY)
      ) {
        this.canvas.style.cursor = "grab";
      } else if (
        this.isPointOnVerticalScrollbarTrack(e.offsetX, e.offsetY) ||
        this.isPointOnHorizontalScrollbarTrack(e.offsetX, e.offsetY)
      ) {
        this.canvas.style.cursor = "pointer";
      } else {
        const rulerY = this.canvas.offsetHeight - this.rulerHeight;
        const isInRuler = e.offsetY >= rulerY;

        if (isInRuler) {
          // Check if active region group is read-only
          const activeGroup = this.timeline.activeRegionGroupId
            ? this.timeline.regionGroups.get(this.timeline.activeRegionGroupId)
            : null;
          const isReadOnly = activeGroup && activeGroup.readOnly;

          // Check for region handles first
          const regionHandle = this.getRegionHandleAtPoint(
            e.offsetX,
            e.offsetY
          );
          if (regionHandle) {
            this.canvas.style.cursor = isReadOnly ? "default" : "ew-resize";
            this.hoveredRegionIndex = regionHandle.index;
          } else {
            const regionAtPoint = this.getRegionAtPoint(e.offsetX, e.offsetY);
            if (regionAtPoint) {
              this.canvas.style.cursor = isReadOnly ? "default" : "grab";
              this.hoveredRegionIndex = regionAtPoint.index;
            } else {
              this.hoveredRegionIndex = null;
              // Check temporary selection handles
              const handle = this.getSelectionHandleAtPoint(
                e.offsetX,
                e.offsetY
              );
              if (handle) {
                this.canvas.style.cursor = "ew-resize";
              } else if (this.isPointInSelection(e.offsetX, e.offsetY)) {
                this.canvas.style.cursor = "grab";
              } else {
                this.canvas.style.cursor = isReadOnly ? "default" : "crosshair";
              }
            }
          }
          this.scheduleRender();
        } else {
          this.hoveredRegionIndex = null;
          if (this.interactionMode === "annotate") {
            // Nothing in a lane is grabbable in this mode.
            this.canvas.style.cursor = "crosshair";
          } else {
            const trackInfo = this.getTrackAtPoint(e.offsetX, e.offsetY);
            this.canvas.style.cursor = trackInfo ? "grab" : "crosshair";
          }
        }
      }
    }
  }

  handleMouseUp(e) {
    if (this.scrollbarDragState) {
      this.scrollbarDragState = null;
      this.canvas.style.cursor = "default";
      this.scheduleRender();
      return;
    }

    if (this.horizontalScrollbarDragState) {
      this.horizontalScrollbarDragState = null;
      this.canvas.style.cursor = "default";
      this.scheduleRender();
      return;
    }

    // Complete region operation
    if (this.regionDragState) {
      const { changed } = this.regionDragState;
      // Merge overlapping regions after drag is complete
      this.timeline.mergeOverlappingRegions(this.timeline.activeRegionGroupId);

      this.regionDragState = null;
      this.canvas.style.cursor = "crosshair";
      this.scheduleRender();

      // A click on a region that never moved isn't a change.
      if (changed && this.daw) {
        const groupId = this.timeline.activeRegionGroupId;
        // Final drag tick: the throttle may have swallowed the last position,
        // and the merge can reshape regions after it.
        const regions = this.daw.getRegions(groupId);
        if (this.daw.onRegionsDrag) this.daw.onRegionsDrag(groupId, regions);
        if (this.daw.onRegionsChange) this.daw.onRegionsChange(groupId, regions);
      }
      return;
    }

    // Complete selection operation
    if (this.selectionDragState) {
      // If selection is too small (less than 0.01 seconds), clear it
      if (
        this.timeline.selection &&
        Math.abs(this.timeline.selection.end - this.timeline.selection.start) <
          0.01
      ) {
        // Too small to be a region, so it was a click. In annotate mode that
        // means seek — the lane behaves like the ruler for dragging and like
        // empty space for clicking.
        if (this.selectionDragState.seekOnClick && this.daw) {
          this.daw.seek(this.selectionDragState.startTime);
        }
        this.timeline.selection = null;
        if (this.daw && this.daw.onSelectionChange) {
          this.daw.onSelectionChange(null);
        }
      } else if (this.timeline.selection) {
        // Add completed selection to active region group
        this.timeline.addRegionToActiveGroup(
          this.timeline.selection.start,
          this.timeline.selection.end
        );
        this.timeline.selection = null; // Clear temporary selection

        if (this.daw && this.daw.onRegionsChange) {
          this.daw.onRegionsChange(
            this.timeline.activeRegionGroupId,
            this.daw.getRegions(this.timeline.activeRegionGroupId)
          );
        }
      }
      this.selectionDragState = null;
      this.canvas.style.cursor = "crosshair";
      this.scheduleRender();
      return;
    }

    if (!this.dragState) return;

    if (this.dragState.isDragging) {
      this.canvas.style.cursor = "crosshair";

      // Pressed on a track but never moved: treat it as a click and seek,
      // rather than reporting an offset change to the value it already had.
      if (!this.dragState.moved) {
        if (this.daw) this.daw.seek(this.dragState.startMouseTime);
        this.dragState = null;
        this.scheduleRender();
        return;
      }

      if (this.daw && this.daw.isPlaying) {
        this.restartTrackDuringPlayback(this.dragState.track);
      }

      // Recalculate duration (track may have been dragged to extend timeline)
      if (this.daw) {
        this.daw.recalculateDuration();
      }

      // Fire track offset change event
      if (this.daw && this.daw.onTrackOffsetChange) {
        this.daw.onTrackOffsetChange(
          this.dragState.track.id,
          this.dragState.track.timeOffset
        );
      }

      this.dragState = null;
      this.scheduleRender();
    } else {
      if (this.daw) {
        const seekTime = this.dragState.clickTime;
        this.daw.seek(seekTime);

        this.dragState = null;
        this.scheduleRender();
      } else {
        this.dragState = null;
      }
    }
  }

  handleDoubleClick(e) {
    // Double-click to clear selection
    if (this.timeline.selection) {
      this.timeline.selection = null;
      this.scheduleRender();
      if (this.daw && this.daw.onSelectionChange) {
        this.daw.onSelectionChange(null, null);
      }
    }
  }

  handleContextMenu(e) {
    e.preventDefault(); // Prevent default context menu

    // Check if right-clicking on a region (works anywhere on canvas now)
    let regionAtPoint = this.getRegionAtPointAnywhere(e.offsetX, e.offsetY);

    // Nothing in the active group at this point — a different, interactable,
    // visible group's region right-clicked anywhere still switches to it
    // first, same as a left-click does, so the menu that opens is for the
    // region actually under the cursor.
    if (!regionAtPoint) {
      const elsewhere = this.findInteractableRegionAt(e.offsetX);
      if (elsewhere && this.daw) {
        this.daw.setActiveRegionGroup(elsewhere.groupId);
        regionAtPoint = this.getRegionAtPointAnywhere(e.offsetX, e.offsetY);
      }
    }

    if (regionAtPoint) {
      const activeGroup = this.timeline.activeRegionGroupId
        ? this.timeline.regionGroups.get(this.timeline.activeRegionGroupId)
        : null;

      // Show context menu
      this.showRegionContextMenu(
        e.clientX,
        e.clientY,
        regionAtPoint,
        activeGroup
      );
    }
  }

  showRegionContextMenu(x, y, regionAtPoint, activeGroup) {
    // Remove any existing context menu
    this.hideContextMenu();

    // Create context menu
    const menu = document.createElement("div");
    menu.id = "daw-context-menu";
    menu.style.cssText = `
      position: fixed;
      left: ${x}px;
      top: ${y}px;
      background: ${this.theme.selectionMenu.background || "#2c2c2c"};
      border: 1px solid ${this.theme.selectionMenu.border || "#444"};
      border-radius: 4px;
      box-shadow: ${
        this.theme.selectionMenu.boxShadow || "0 4px 12px rgba(0, 0, 0, 0.5)"
      };
      padding: 4px 0;
      min-width: 180px;
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif;
      font-size: ${this.theme.selectionMenu.fontSize || "13px"};
      color: ${this.theme.selectionMenu.color || "#fff"};
    `;

    // Add menu items
    const isReadOnly = activeGroup && activeGroup.readOnly;
    const region = regionAtPoint.region;

    // Remove option
    const removeItem = document.createElement("div");
    removeItem.textContent = "Remove Region";
    removeItem.style.cssText = `
      padding: 8px 16px;
      cursor: ${isReadOnly ? "not-allowed" : "pointer"};
      color: ${
        isReadOnly
          ? this.theme.selectionMenu.readOnlyTextColor || "#666"
          : this.theme.selectionMenu.textColor || "#fff"
      };
    `;

    if (!isReadOnly) {
      removeItem.addEventListener("mouseenter", () => {
        removeItem.style.background =
          this.theme.selectionMenu.mouseEnterBackground || "#404040";
      });
      removeItem.addEventListener("mouseleave", () => {
        removeItem.style.background =
          this.theme.selectionMenu.mouseLeaveBackground || "transparent";
      });
      removeItem.addEventListener("click", () => {
        this.timeline.removeRegionFromActiveGroup(regionAtPoint.index);
        this.scheduleRender();

        // Trigger onRegionsChange callback
        if (
          this.daw &&
          this.daw.onRegionsChange &&
          this.timeline.activeRegionGroupId
        ) {
          this.daw.onRegionsChange(
            this.timeline.activeRegionGroupId,
            this.daw.getRegions(this.timeline.activeRegionGroupId)
          );
        }

        if (this.daw) {
          this.daw.log(`Removed region at index ${regionAtPoint.index}`);
        }
        this.hideContextMenu();
      });
    } else {
      // If read-only, still close menu when clicked
      removeItem.addEventListener("click", () => {
        this.hideContextMenu();
      });
    }

    menu.appendChild(removeItem);

    // Add separator
    const separator = document.createElement("div");
    separator.style.cssText = `
      height: 1px;
      background: ${this.theme.selectionMenu.separatorBackground || "#444"};
      margin: 4px 0;
    `;
    menu.appendChild(separator);

    // Add info item (non-clickable)
    const infoItem = document.createElement("div");
    const startTime = region.start.toFixed(2);
    const endTime = region.end.toFixed(2);
    const duration = (region.end - region.start).toFixed(2);
    infoItem.innerHTML = `
      <div style="padding: 4px 16px; color: ${
        this.theme.selectionMenu.detailsTextColor || "#999"
      }; font-size: ${this.theme.selectionMenu.detailsFontSize || "11px"};">
        Start: ${startTime}s<br>
        End: ${endTime}s<br>
        Duration: ${duration}s
      </div>
    `;
    menu.appendChild(infoItem);

    document.body.appendChild(menu);

    // Store reference for cleanup
    this.contextMenu = menu;

    // Close menu when clicking outside
    const closeHandler = (event) => {
      if (!menu.contains(event.target)) {
        this.hideContextMenu();
        document.removeEventListener("mousedown", closeHandler);
      }
    };
    setTimeout(() => {
      document.addEventListener("mousedown", closeHandler);
    }, 0);
  }

  hideContextMenu() {
    if (this.contextMenu) {
      this.contextMenu.remove();
      this.contextMenu = null;
    }
  }

  handleKeyDown(e) {
    // Escape key to clear selection
    if (e.key === "Escape" && this.timeline.selection) {
      this.timeline.selection = null;
      this.scheduleRender();
      if (this.daw && this.daw.onSelectionChange) {
        this.daw.onSelectionChange(null, null);
      }
    }
  }

  restartTrackDuringPlayback(track) {
    // Re-align this track against the transport after its offset moved. Only
    // this track is touched — the others are already where they should be.
    track.source?.seek(this.daw.currentTime, this.daw.playbackSpeed, this.daw.isPlaying);
  }

  scheduleRender() {
    if (this.renderScheduled) return;
    this.renderScheduled = true;
    requestAnimationFrame(() => {
      this.render();
      this.renderScheduled = false;
    });
  }

  render() {
    const width = this.canvas.offsetWidth;
    const height = this.canvas.offsetHeight;

    this.ctx.clearRect(0, 0, width, height);

    this.drawBackground();
    this.drawGrid();
    this.drawTracks();
    this.drawTimelineRuler();
    this.drawHorizontalScrollbar();
    this.drawSelection();
    this.drawPlayhead();

    if (this.minimapCanvas) {
      this.renderMinimap();
    }

    this.emitLayoutChange();
  }

  renderMinimap() {
    const width = this.minimapCanvas.offsetWidth;
    const height = this.minimapCanvas.offsetHeight;

    this.minimapCtx.clearRect(0, 0, width, height);

    this.minimapCtx.fillStyle = this.theme.minimapBackground;
    this.minimapCtx.fillRect(0, 0, width, height);

    const totalDuration = this.timeline.duration || 10;
    const pixelsPerSecond = width / totalDuration;
    const dpr = this.getMinimapPixelRatio();
    const columnCount = Math.max(1, Math.ceil(width * dpr));

    // Bands fill the canvas's actual height, the same way calculateTrackHeight()
    // does for the main view — a fixed pixel band regardless of how tall the
    // host makes this canvas left most of it empty and pinned the waveform to
    // a sliver at the top.
    const visibleTracks = this.getVisibleTracks();
    const topPadding = 4;
    const trackSpacing = 2;
    const numTracks = visibleTracks.length;
    const availableHeight = Math.max(
      0,
      height - topPadding * 2 - trackSpacing * Math.max(0, numTracks - 1)
    );
    const trackHeight = numTracks > 0 ? Math.max(4, availableHeight / numTracks) : 0;
    const innerPad = Math.min(3, trackHeight / 4);

    visibleTracks.forEach((track, index) => {
      if (!track.hasAudio()) return;

      const trackY = topPadding + index * (trackHeight + trackSpacing);

      const startX = track.timeOffset * pixelsPerSecond;
      const trackWidth = track.duration * pixelsPerSecond;

      this.minimapCtx.fillStyle = this.theme.minimapTrackBackground;
      this.minimapCtx.fillRect(startX, trackY, trackWidth, trackHeight);

      // Real peaks once the waveform source has something resident (which for
      // a remote track means at least the overview level has landed); a flat
      // bar otherwise, same as the loading state elsewhere.
      const drewWaveform =
        track.waveform &&
        this.drawMinimapWaveform(
          track,
          trackY + innerPad,
          trackHeight - innerPad * 2,
          pixelsPerSecond,
          dpr,
          columnCount
        );

      if (!drewWaveform) {
        this.minimapCtx.fillStyle =
          track.color || this.theme.defaultWaveformColor;
        this.minimapCtx.globalAlpha = this.theme.minimapWaveformOpacity;
        this.minimapCtx.fillRect(startX, trackY + innerPad, trackWidth, trackHeight - innerPad * 2);
        this.minimapCtx.globalAlpha = 1.0;
      }
    });

    const viewportStartX = this.timeline.viewportScrollTime * pixelsPerSecond;
    const viewportWidth = this.timeline.getVisibleDuration() * pixelsPerSecond;

    this.minimapCtx.fillStyle = this.theme.minimapViewportFill;
    this.minimapCtx.fillRect(viewportStartX, 0, viewportWidth, height);

    this.minimapCtx.strokeStyle = this.theme.minimapViewportStroke;
    this.minimapCtx.lineWidth = this.theme.minimapViewportStrokeWidth;
    this.minimapCtx.strokeRect(viewportStartX, 0, viewportWidth, height);

    // Draw regions in minimap — same multi-group, active-vs-dimmed-vs-bar
    // treatment as the main lanes, mirrored here per "Multi-group rendering".
    const activeId = this.timeline.activeRegionGroupId;
    for (const [groupId, group] of this.timeline.getVisibleRegionGroupEntries()) {
      const isActive = groupId === activeId;
      const color = group.color || this.theme.selectionBorder;

      if (!isActive && this.regionDisplayMode === "minimized") {
        const barHeight = this.theme.regionGroupBarHeight;
        // No ruler here, so "bottom" just means the minimap's own bottom edge.
        const barY = this.theme.regionGroupBarPosition === "bottom" ? height - barHeight : 0;
        this.minimapCtx.fillStyle = color;
        for (const region of group.regions) {
          const regStartX = region.start * pixelsPerSecond;
          const regEndX = region.end * pixelsPerSecond;
          this.minimapCtx.fillRect(regStartX, barY, regEndX - regStartX, barHeight);
        }
        continue;
      }

      const dim = isActive ? 1 : this.theme.inactiveRegionGroupOpacity;
      for (const region of group.regions) {
        const regStartX = region.start * pixelsPerSecond;
        const regEndX = region.end * pixelsPerSecond;

        this.minimapCtx.globalAlpha = this.theme.selectionFillOpacity * dim;
        this.minimapCtx.fillStyle = color;
        this.minimapCtx.fillRect(regStartX, 0, regEndX - regStartX, height);

        this.minimapCtx.globalAlpha = dim;
        this.minimapCtx.strokeStyle = color;
        this.minimapCtx.lineWidth = 2;
        this.minimapCtx.strokeRect(regStartX, 0, regEndX - regStartX, height);
      }
    }
    this.minimapCtx.globalAlpha = 1;

    // Draw temporary selection in minimap (while dragging) — always the
    // active group's colour, drawn in full.
    if (this.timeline.selection) {
      const activeGroup = this.getActiveGroup();
      const color = (activeGroup && activeGroup.color) || this.theme.selectionBorder;
      const selStartX = this.timeline.selection.start * pixelsPerSecond;
      const selEndX = this.timeline.selection.end * pixelsPerSecond;

      this.minimapCtx.globalAlpha = this.theme.selectionFillOpacity;
      this.minimapCtx.fillStyle = color;
      this.minimapCtx.fillRect(selStartX, 0, selEndX - selStartX, height);

      this.minimapCtx.globalAlpha = 1;
      this.minimapCtx.strokeStyle = color;
      this.minimapCtx.lineWidth = 2;
      this.minimapCtx.strokeRect(selStartX, 0, selEndX - selStartX, height);
    }

    if (this.daw) {
      const playheadX = this.daw.currentTime * pixelsPerSecond;
      this.minimapCtx.strokeStyle = this.theme.minimapPlayheadColor;
      this.minimapCtx.lineWidth = this.theme.minimapPlayheadWidth;
      this.minimapCtx.beginPath();
      this.minimapCtx.moveTo(playheadX, 0);
      this.minimapCtx.lineTo(playheadX, height);
      this.minimapCtx.stroke();
    }
  }

  handleMinimapMouseDown(e) {
    const width = this.minimapCanvas.offsetWidth;
    const totalDuration = this.timeline.duration || 10;
    const pixelsPerSecond = width / totalDuration;

    const clickTime = e.offsetX / pixelsPerSecond;
    const viewportWidth = this.timeline.getVisibleDuration();

    const viewportStartX = this.timeline.viewportScrollTime * pixelsPerSecond;
    const viewportEndX =
      (this.timeline.viewportScrollTime + viewportWidth) * pixelsPerSecond;

    if (e.offsetX >= viewportStartX && e.offsetX <= viewportEndX) {
      this.minimapDragState = {
        isDragging: true,
        startMouseX: e.offsetX,
        startScrollTime: this.timeline.viewportScrollTime,
      };
      this.minimapCanvas.style.cursor = "grabbing";
    } else {
      const newScrollTime = clickTime - viewportWidth / 2;
      this.scrollToTime(newScrollTime);
    }
  }

  handleMinimapMouseMove(e) {
    if (!this.minimapDragState || !this.minimapDragState.isDragging) {
      const width = this.minimapCanvas.offsetWidth;
      const totalDuration = this.timeline.duration || 10;
      const pixelsPerSecond = width / totalDuration;
      const viewportWidth = this.timeline.getVisibleDuration();
      const viewportStartX = this.timeline.viewportScrollTime * pixelsPerSecond;
      const viewportEndX =
        (this.timeline.viewportScrollTime + viewportWidth) * pixelsPerSecond;

      if (e.offsetX >= viewportStartX && e.offsetX <= viewportEndX) {
        this.minimapCanvas.style.cursor = "grab";
      } else {
        this.minimapCanvas.style.cursor = "pointer";
      }
      return;
    }

    const width = this.minimapCanvas.offsetWidth;
    const totalDuration = this.timeline.duration || 10;
    const pixelsPerSecond = width / totalDuration;

    const deltaX = e.offsetX - this.minimapDragState.startMouseX;
    const deltaTime = deltaX / pixelsPerSecond;

    const newScrollTime = this.minimapDragState.startScrollTime + deltaTime;
    this.scrollToTime(newScrollTime);
  }

  handleMinimapMouseUp(e) {
    if (this.minimapDragState) {
      this.minimapDragState = null;
      this.minimapCanvas.style.cursor = "pointer";
    }
  }

  scrollToTime(time) {
    const visibleDuration = this.timeline.getVisibleDuration();
    const effectiveDuration = this.timeline.getEffectiveDuration(
      this.canvas.offsetWidth
    );
    const maxScroll = Math.max(0, effectiveDuration - visibleDuration);

    this.timeline.viewportScrollTime = Math.max(0, Math.min(maxScroll, time));
    this.timeline.updateViewport(this.canvas.offsetWidth);
    this.scheduleRender();

    if (this.daw && this.daw.onScrollChange) {
      this.daw.onScrollChange(this.timeline.viewportScrollTime);
    }
  }

  drawBackground() {
    const width = this.canvas.offsetWidth;
    const height = this.canvas.offsetHeight;

    this.ctx.fillStyle = this.theme.background;
    this.ctx.fillRect(0, 0, width, height);
  }

  drawGrid() {
    const width = this.canvas.offsetWidth;
    const height = this.canvas.offsetHeight - this.rulerHeight;

    let gridInterval = 1;
    if (this.timeline.pixelsPerSecond < 20) {
      gridInterval = 5;
    } else if (this.timeline.pixelsPerSecond > 100) {
      gridInterval = 0.5;
    }

    this.ctx.strokeStyle = this.theme.gridColor;
    this.ctx.lineWidth = this.theme.gridLineWidth;

    for (
      let time =
        Math.floor(this.timeline.viewportStartTime / gridInterval) *
        gridInterval;
      time <= this.timeline.viewportEndTime;
      time += gridInterval
    ) {
      const x = this.timeline.timeToCanvasX(time);
      this.ctx.beginPath();
      this.ctx.moveTo(x, 0);
      this.ctx.lineTo(x, height);
      this.ctx.stroke();
    }
  }

  drawTracks() {
    const trackHeight = this.calculateTrackHeight();

    this.ctx.save();

    const viewportHeight = this.canvas.offsetHeight - this.rulerHeight;
    this.ctx.beginPath();
    this.ctx.rect(0, 0, this.canvas.offsetWidth, viewportHeight);
    this.ctx.clip();

    this.getVisibleTracks().forEach((track, index) => {
      const trackY = 20 + index * (trackHeight + this.trackSpacing);
      const displayY = trackY - this.verticalScrollOffset;

      if (displayY + trackHeight >= 0 && displayY <= viewportHeight) {
        this.drawTrack(track, displayY, trackHeight);
      }
    });

    this.ctx.restore();
    this.drawVerticalScrollbar();
  }

  /**
   * The backing store scale actually in effect, read off the canvas rather than
   * `window.devicePixelRatio` — the two disagree between a display change and
   * the resize that reacts to it, and it's the backing store that decides where
   * a device pixel really is.
   */
  getPixelRatio() {
    const cssWidth = this.canvas.offsetWidth;
    if (!cssWidth) return window.devicePixelRatio || 1;
    return this.canvas.width / cssWidth;
  }

  getMinimapPixelRatio() {
    const cssWidth = this.minimapCanvas.offsetWidth;
    if (!cssWidth) return window.devicePixelRatio || 1;
    return this.minimapCanvas.width / cssWidth;
  }

  /**
   * Column accumulators for the waveform, grown on demand and reused — this is
   * per track per frame, so allocating here would mean garbage every frame.
   */
  getColumnScratch(columnCount) {
    if (!this.columnScratch || this.columnScratch.mins.length < columnCount) {
      this.columnScratch = {
        mins: new Float32Array(columnCount),
        maxs: new Float32Array(columnCount),
      };
    }
    return this.columnScratch;
  }

  /**
   * Bridge columns no peak landed in, by interpolating the envelope across them.
   *
   * Only happens under magnification — zoomed in past the finest level, where
   * there are more columns than peaks. Carrying the previous column forward
   * instead would stair-step, and leaving the gaps gives a dashed waveform.
   */
  fillEmptyColumns(mins, maxs, firstColumn, lastColumn) {
    let previous = firstColumn;

    for (let column = firstColumn + 1; column <= lastColumn; column++) {
      if (mins[column] > maxs[column]) continue; // nothing landed here

      const gap = column - previous;
      if (gap > 1) {
        const minStep = (mins[column] - mins[previous]) / gap;
        const maxStep = (maxs[column] - maxs[previous]) / gap;

        for (let i = 1; i < gap; i++) {
          mins[previous + i] = mins[previous] + minStep * i;
          maxs[previous + i] = maxs[previous] + maxStep * i;
        }
      }

      previous = column;
    }
  }

  /**
   * Box-filtered waveform thumbnail for one minimap track lane, from the same
   * peak source and column-accumulation approach as drawTrack — just spanning
   * the track's whole duration instead of the current viewport.
   *
   * Returns false (drawing nothing) when the waveform has no resident level
   * yet for this range, so the caller can fall back to its flat placeholder
   * instead of leaving the lane blank.
   */
  drawMinimapWaveform(track, y, height, pixelsPerSecond, dpr, columnCount) {
    const samplesPerPixel = track.sampleRate / pixelsPerSecond;
    const view = track.waveform.getView(0, track.sampleCount, samplesPerPixel);
    if (!view) return false;

    const { level, startPeak, endPeak } = view;
    const samplesPerPeak = level.samplesPerPeak;
    const secondsPerPeak = samplesPerPeak / track.sampleRate;
    const originX = track.timeOffset * pixelsPerSecond;

    const { mins, maxs } = this.getColumnScratch(columnCount);
    mins.fill(Infinity, 0, columnCount);
    maxs.fill(-Infinity, 0, columnCount);

    let firstColumn = columnCount;
    let lastColumn = -1;

    for (const chunk of level.chunks(startPeak, endPeak)) {
      const { data, offset, count, firstPeak } = chunk;

      for (let k = 0; k < count; k++) {
        const x = originX + (firstPeak + k) * secondsPerPeak * pixelsPerSecond;
        const column = Math.floor(x * dpr);
        if (column < 0 || column >= columnCount) continue;

        const peakMin = data[offset + k * 2] / 32767;
        const peakMax = data[offset + k * 2 + 1] / 32767;

        if (peakMin < mins[column]) mins[column] = peakMin;
        if (peakMax > maxs[column]) maxs[column] = peakMax;

        if (column < firstColumn) firstColumn = column;
        if (column > lastColumn) lastColumn = column;
      }
    }

    if (lastColumn < firstColumn) return false;

    this.fillEmptyColumns(mins, maxs, firstColumn, lastColumn);

    const devicePixel = 1 / dpr;

    this.minimapCtx.fillStyle = track.color || this.theme.defaultWaveformColor;
    this.minimapCtx.globalAlpha = this.theme.minimapWaveformOpacity;
    this.minimapCtx.beginPath();

    for (let column = firstColumn; column <= lastColumn; column++) {
      const peakMin = mins[column];
      const peakMax = maxs[column];

      const y1 = y + height * (1 - (peakMax + 1) / 2);
      const y2 = y + height * (1 - (peakMin + 1) / 2);

      this.minimapCtx.rect(column * devicePixel, y1, devicePixel, Math.max(y2 - y1, devicePixel));
    }

    this.minimapCtx.fill();
    this.minimapCtx.globalAlpha = 1.0;

    return true;
  }

  drawTrack(track, y, height) {
    if (!track.hasAudio() || !track.waveform) {
      // Draw loading indicator if track is loading
      if (track.isLoading) {
        const width = this.canvas.offsetWidth;
        this.ctx.fillStyle = this.theme.trackBackground;
        this.ctx.fillRect(0, y, width, height);

        this.ctx.fillStyle = this.theme.loadingTextColor;
        this.ctx.font = this.theme.loadingTextFont;
        this.ctx.textAlign = "center";

        // Show specific loading phase
        let loadingText = "Loading...";
        if (track.loadingPhase === "audio") {
          loadingText = "Loading audio...";
        } else if (track.loadingPhase === "waveform") {
          loadingText = "Generating waveform...";
        }

        this.ctx.fillText(loadingText, width / 2, y + height / 2);
        this.ctx.textAlign = "left";
      }
      return;
    }

    const width = this.canvas.offsetWidth;

    const trackStartTime = track.timeOffset;
    const trackEndTime = track.timeOffset + track.duration;

    const trackStartX = this.timeline.timeToCanvasX(trackStartTime);
    const trackEndX = this.timeline.timeToCanvasX(trackEndTime);

    const bgStartX = Math.max(0, trackStartX);
    const bgEndX = Math.min(width, trackEndX);

    if (bgEndX > bgStartX) {
      const isBeingDragged =
        this.dragState &&
        this.dragState.isDragging &&
        this.dragState.track === track;

      this.ctx.fillStyle = isBeingDragged
        ? this.theme.trackBackgroundDragging
        : this.theme.trackBackground;
      this.ctx.fillRect(bgStartX, y, bgEndX - bgStartX, height);

      if (isBeingDragged) {
        this.ctx.strokeStyle = this.theme.trackBorderColor;
        this.ctx.lineWidth = this.theme.trackBorderWidth;
        this.ctx.strokeRect(bgStartX, y, bgEndX - bgStartX, height);
      }
    }

    const startTime = Math.max(
      0,
      this.timeline.viewportStartTime - track.timeOffset
    );
    const endTime = this.timeline.viewportEndTime - track.timeOffset;

    if (endTime < 0 || startTime > track.duration) return;

    // Ask the source for the best peaks it has for this range. It may hand back
    // a coarser level than ideal while finer blocks are still in flight —
    // deliberately, so the waveform never blanks mid-navigation.
    const startSample = Math.max(0, Math.floor(startTime * track.sampleRate));
    const endSample = Math.min(track.sampleCount, Math.ceil(endTime * track.sampleRate));
    const samplesPerPixel = track.sampleRate / this.timeline.pixelsPerSecond;

    const view = track.waveform.getView(startSample, endSample, samplesPerPixel);
    if (!view) return;

    const { level, startPeak, endPeak } = view;
    const samplesPerPeak = level.samplesPerPeak;

    // Log mipmap level changes
    const lastResolution = this.lastMipmapLevels.get(track.id);
    if (lastResolution !== samplesPerPeak) {
      if (this.daw) {
        this.daw.log(
          `[Mipmap] Track "${track.fileName}" (${track.id}) switched from 1/${
            lastResolution || "none"
          } to 1/${samplesPerPeak} (samplesPerPixel: ${samplesPerPixel.toFixed(
            1
          )}, zoom: ${this.timeline.pixelsPerSecond.toFixed(1)}px/s)`
        );
      }
      this.lastMipmapLevels.set(track.id, samplesPerPeak);
    }

    // Calculate gain multiplier once (if waveform gain scaling is enabled)
    const gainMultiplier =
      this.theme.waveformGainScaling && track.gain !== 0
        ? Math.pow(10, track.gain / 20)
        : 1.0;
    const scaleGain = this.theme.waveformGainScaling && gainMultiplier !== 1.0;
    // See theme.waveformGainCurve: the soft knee keeps a boosted waveform inside
    // the lane, the linear one keeps it honest against the dB ruler.
    const softKnee = this.theme.waveformGainCurve !== "linear";

    // Hoisted out of the loop: this runs up to ~10k times per track per frame.
    const secondsPerPeak = samplesPerPeak / track.sampleRate;
    const viewportStart = this.timeline.viewportStartTime;
    const pixelsPerSecond = this.timeline.pixelsPerSecond;
    const originX = (track.timeOffset - viewportStart) * pixelsPerSecond;

    // Box-filter the peaks onto the device pixel grid. Drawing one line per peak
    // instead aliases badly: the ladder steps by decades, so peaks-per-column
    // lands anywhere in [1, 10), and the beat between that pitch and the pixel
    // pitch crawls across the screen as the viewport scrolls.
    const dpr = this.getPixelRatio();
    const columnCount = Math.max(1, Math.ceil(width * dpr));
    const { mins, maxs } = this.getColumnScratch(columnCount);

    mins.fill(Infinity, 0, columnCount);
    maxs.fill(-Infinity, 0, columnCount);

    let firstColumn = columnCount;
    let lastColumn = -1;

    for (const chunk of level.chunks(startPeak, endPeak)) {
      const { data, offset, count, firstPeak } = chunk;

      for (let k = 0; k < count; k++) {
        const x = originX + (firstPeak + k) * secondsPerPeak * pixelsPerSecond;
        const column = Math.floor(x * dpr);
        if (column < 0 || column >= columnCount) continue;

        // Raw here, gain applied per column below: tanh is monotonic, so
        // tanh(min(·)) === min(tanh(·)), and this way it runs ~width times
        // rather than once per peak.
        const peakMin = data[offset + k * 2] / 32767;
        const peakMax = data[offset + k * 2 + 1] / 32767;

        if (peakMin < mins[column]) mins[column] = peakMin;
        if (peakMax > maxs[column]) maxs[column] = peakMax;

        if (column < firstColumn) firstColumn = column;
        if (column > lastColumn) lastColumn = column;
      }
    }

    // Muted, or silenced by a solo elsewhere. Dimmed rather than hidden — the
    // audio is still there and still worth reading, and a track that looks
    // identical whether or not it can be heard is a support call waiting to
    // happen.
    const audible = this.timeline.isTrackAudible(track);

    this.ctx.save();
    if (!audible) this.ctx.globalAlpha = this.theme.inaudibleTrackOpacity;

    if (lastColumn >= firstColumn) {
      this.fillEmptyColumns(mins, maxs, firstColumn, lastColumn);

      // Axis-aligned rects on exact device pixels: no antialiasing, so a
      // column's brightness no longer depends on its subpixel phase.
      const devicePixel = 1 / dpr;
      const barWidth = Math.max(1, this.theme.waveformLineWidth) * devicePixel;

      this.ctx.fillStyle = track.color || this.theme.defaultWaveformColor;
      this.ctx.beginPath();

      for (let column = firstColumn; column <= lastColumn; column++) {
        let peakMin = mins[column];
        let peakMax = maxs[column];

        // Apply gain scaling with soft clipping using tanh
        if (scaleGain) {
          peakMax = softKnee
            ? Math.tanh(peakMax * gainMultiplier)
            : Math.min(1, peakMax * gainMultiplier);
          peakMin = softKnee
            ? Math.tanh(peakMin * gainMultiplier)
            : Math.max(-1, peakMin * gainMultiplier);
        }

        const y1 = y + height * (1 - (peakMax + 1) / 2);
        const y2 = y + height * (1 - (peakMin + 1) / 2);

        // Silence is a real reading, so give it a visible centre line rather
        // than a zero-height rect that fills nothing.
        this.ctx.rect(column * devicePixel, y1, barWidth, Math.max(y2 - y1, devicePixel));
      }

      this.ctx.fill();
    }

    this.drawSpectrogram(track, y, height, startTime, endTime);
    this.drawFrequencyHighlights(track, y, height);
    this.drawBufferedRange(track, y, height);

    this.ctx.restore();

    if (track.fileName) {
      this.ctx.fillStyle = this.theme.trackLabelColor;
      this.ctx.font = this.theme.trackLabelFont;
      this.ctx.textAlign = "left";

      // Say *why* it's dim. A track silenced by someone else's solo looks the
      // same as a muted one otherwise, and they're undone differently.
      let label = track.fileName;
      if (track.muted) label += "  (muted)";
      else if (track.soloed) label += "  (solo)";
      else if (!audible) label += "  (silenced by solo)";

      // Pin the label to the visible left edge of the track, so it stays put as
      // the track scrolls partly off screen.
      this.ctx.fillText(label, bgStartX + 8, y + 20);
    }

    const isBeingDragged =
      this.dragState &&
      this.dragState.isDragging &&
      this.dragState.track === track;
    if (isBeingDragged) {
      const offsetText = `Offset: ${track.timeOffset.toFixed(3)}s`;
      this.ctx.fillStyle = this.theme.offsetDisplayBackground;
      this.ctx.fillRect(10, y + 40, 140, 24);
      this.ctx.fillStyle = this.theme.offsetDisplayColor;
      this.ctx.font = this.theme.offsetDisplayFont;
      this.ctx.fillText(offsetText, 15, y + 56);
    }
  }

  /**
   * Draw the spectrogram over the waveform, at the track's own opacity.
   *
   * Sits in the same lane rather than its own, so track heights and vertical
   * scrolling are unaffected: opacity 0 is waveform only, 1 covers it entirely.
   *
   * @param {number} startTime - track-local seconds at the left of the viewport
   * @param {number} endTime - track-local seconds at the right of the viewport
   */
  drawSpectrogram(track, y, height, startTime, endTime) {
    const opacity = track.spectrogramOpacity;
    if (!track.spectrogram || opacity <= 0) return;

    const from = Math.max(0, startTime);
    const to = Math.min(track.duration, endTime);
    if (to <= from) return;

    const { tiles } = track.spectrogram.getTiles(from, to, this.timeline.pixelsPerSecond);
    if (tiles.length === 0) return;

    this.ctx.save();

    // Clip to the track's own extent so tiles can't bleed past the end of the
    // audio or over a neighbouring track.
    const clipStart = this.timeline.timeToCanvasX(track.timeOffset);
    const clipEnd = this.timeline.timeToCanvasX(track.timeOffset + track.duration);
    this.ctx.beginPath();
    this.ctx.rect(clipStart, y, clipEnd - clipStart, height);
    this.ctx.clip();

    // Multiplied, not assigned: an inaudible track has already dimmed the
    // context and the overlay has to dim with it.
    this.ctx.globalAlpha *= Math.min(1, opacity);
    this.ctx.imageSmoothingEnabled = this.theme.spectrogramSmoothing !== false;

    // Which rows of the tile to show. The image's `bins` rows divide the analysed
    // range into equal steps *in scale space*, so a band maps to a contiguous
    // row span — and cropping to it is the whole frequency zoom. No server
    // involvement: on a mel axis the low end already holds most of the rows, so
    // zooming a 24 kHz asset to 0-4 kHz still has 84 of the 128 to stretch.
    const band = this.frequencyRangeFor(track);
    const full = {
      min: track.spectrogram.minFrequency,
      max: track.spectrogram.maxFrequency,
    };
    const scale = track.spectrogram.binScale;
    const bins = track.spectrogram.bins;

    const bottom = frequencyToFraction(band.min, full.min, full.max, scale);
    const top = frequencyToFraction(band.max, full.min, full.max, scale);

    // Row 0 is the highest frequency, so the top of the band is the *low* row.
    const sourceY = Math.max(0, (1 - top) * bins);
    const sourceHeight = Math.min(bins - sourceY, (top - bottom) * bins);
    const cropped = sourceHeight > 0 && sourceHeight < bins;

    // Tile edges snap to device pixels. At a fractional edge each tile only
    // part-covers the shared pixel, and two partial covers composite to less
    // than one — so the waveform behind shows through as a faint seam, white on
    // a dark spectrogram. Neighbours compute their shared edge from the same
    // time, so they round to the same pixel and abut exactly.
    const dpr = this.getPixelRatio();
    const snap = (x) => Math.round(x * dpr) / dpr;

    for (const tile of tiles) {
      if (!tile.bitmap) continue;

      const x0 = snap(this.timeline.timeToCanvasX(track.timeOffset + tile.startTime));
      const x1 = snap(this.timeline.timeToCanvasX(track.timeOffset + tile.endTime));
      const width = x1 - x0;
      if (width <= 0) continue;

      if (cropped) {
        this.ctx.drawImage(
          tile.bitmap,
          0,
          sourceY,
          tile.bitmap.width,
          sourceHeight,
          x0,
          y,
          width,
          height
        );
      } else {
        this.ctx.drawImage(tile.bitmap, x0, y, width, height);
      }
    }

    // globalAlpha and imageSmoothingEnabled are part of the saved state, so the
    // restore puts both back — the waveform stroke that follows on the next
    // track must not inherit them.
    this.ctx.restore();
  }

  /**
   * Tint frequency bands across the lane — "where voices are", say.
   *
   * Read from the track, not the DAW: the setting is DAW-wide for now, but each
   * track holds its own copy so making it per-track touches only the setter.
   * Drawn whatever the spectrogram's opacity, since the ruler beside the lane
   * shows the bands either way; limited to the audio's extent, like the
   * spectrogram, because outside it there are no frequencies to point at.
   */
  drawFrequencyHighlights(track, y, height) {
    if (!track.frequencyHighlightsOnLane || !track.frequencyHighlights?.length) return;
    const band = this.frequencyRangeFor(track);
    if (!band) return;

    const scale = track.spectrogram.binScale;
    const x0 = this.timeline.timeToCanvasX(track.timeOffset);
    const x1 = this.timeline.timeToCanvasX(track.timeOffset + track.duration);
    const left = Math.max(0, x0);
    const right = Math.min(this.canvas.offsetWidth, x1);
    if (right <= left) return;

    for (const highlight of track.frequencyHighlights) {
      const top = y + height * (1 - frequencyToFraction(highlight.max, band.min, band.max, scale));
      const bottom = y + height * (1 - frequencyToFraction(highlight.min, band.min, band.max, scale));
      const y0 = Math.max(y, top);
      const y1 = Math.min(y + height, bottom);
      if (y1 <= y0) continue;
      this.ctx.fillStyle = highlight.color;
      this.ctx.fillRect(left, y0, right - left, y1 - y0);
    }
  }

  /**
   * Show how much of a streamed track is actually playable right now.
   *
   * A thin strip along the bottom of the lane rather than dimming the
   * unbuffered part: the waveform is drawn from analysis data and is perfectly
   * valid everywhere, so shading most of the track would say something untrue.
   * What's worth showing is where the audio is, and whether it ran out.
   */
  drawBufferedRange(track, y, height) {
    const ranges = track.source?.getBufferedRanges?.() ?? [];
    if (ranges.length === 0) return;

    // A track whose audio is entirely resident has nothing to report.
    if (ranges.length === 1 && ranges[0].start <= 0 && ranges[0].end >= track.duration) return;

    const barHeight = this.theme.bufferedBarHeight;
    const barY = y + height - barHeight;
    const width = this.canvas.offsetWidth;

    this.ctx.fillStyle = track.source.stalled
      ? this.theme.stalledBarColor
      : this.theme.bufferedBarColor;

    for (const range of ranges) {
      const x0 = Math.max(0, this.timeline.timeToCanvasX(track.timeOffset + range.start));
      const x1 = Math.min(width, this.timeline.timeToCanvasX(track.timeOffset + range.end));
      if (x1 > x0) this.ctx.fillRect(x0, barY, x1 - x0, barHeight);
    }
  }

  drawTimelineRuler() {
    const width = this.canvas.offsetWidth;
    const height = this.canvas.offsetHeight;
    const rulerY = height - this.rulerHeight;

    this.ctx.fillStyle = this.theme.rulerBackground;
    this.ctx.fillRect(0, rulerY, width, this.rulerHeight);

    // Dynamically calculate tick interval to keep labels readable
    // Target: 60-120 pixels between major ticks
    const targetPixelsBetweenTicks = 80;
    const secondsBetweenTicks =
      targetPixelsBetweenTicks / this.timeline.pixelsPerSecond;

    // Round to nice intervals: 0.1, 0.5, 1, 5, 10, 30, 60, 300, 600, 1800, 3600, etc.
    const niceIntervals = [
      0.1, 0.5, 1, 5, 10, 30, 60, 300, 600, 1800, 3600, 7200, 18000, 36000,
    ];
    let majorTickInterval = niceIntervals[0];

    for (let i = 0; i < niceIntervals.length; i++) {
      if (niceIntervals[i] >= secondsBetweenTicks) {
        majorTickInterval = niceIntervals[i];
        break;
      }
      if (i === niceIntervals.length - 1) {
        majorTickInterval = niceIntervals[i];
      }
    }

    const minorTickInterval = majorTickInterval / 4;

    this.ctx.strokeStyle = this.theme.rulerTickColor;
    this.ctx.fillStyle = this.theme.rulerTextColor;
    this.ctx.font = this.theme.rulerTextFont;
    this.ctx.textAlign = "center";

    for (
      let time =
        Math.floor(this.timeline.viewportStartTime / majorTickInterval) *
        majorTickInterval;
      time <= this.timeline.viewportEndTime;
      time += minorTickInterval
    ) {
      const x = this.timeline.timeToCanvasX(time);
      const isMajor = Math.abs(time % majorTickInterval) < 0.001;

      // Calculate tick start position - with configurable bottom margin
      const tickStartY = height - (this.theme.rulerTickBottomMargin || 0);
      const tickHeight = isMajor
        ? this.theme.rulerMajorTickHeight
        : this.theme.rulerMinorTickHeight;
      const tickEndY = rulerY + this.rulerHeight - tickHeight;

      this.ctx.beginPath();
      this.ctx.moveTo(x, tickStartY);
      this.ctx.lineTo(x, tickEndY);
      this.ctx.stroke();

      if (isMajor) {
        const label = this.formatTime(time);
        this.ctx.fillText(label, x, rulerY + 12);
      }
    }
  }

  shouldShowVerticalScrollbar() {
    return this.theme.scrollbar.showVertical && this.getMaxVerticalScroll() > 0;
  }

  shouldShowHorizontalScrollbar() {
    return this.theme.scrollbar.showHorizontal && this.getMaxHorizontalScroll() > 0;
  }

  getVerticalScrollbarGeometry() {
    const scrollbarWidth = this.theme.scrollbar.width;
    const scrollbarX = this.canvas.offsetWidth - scrollbarWidth - 4;
    const scrollbarY = 4;

    // Subtract ruler and horizontal scrollbar space from available height
    const horizontalScrollbarSpace = this.shouldShowHorizontalScrollbar()
      ? this.theme.scrollbar.width + 8
      : 0;
    const scrollbarHeight =
      this.canvas.offsetHeight -
      this.rulerHeight -
      horizontalScrollbarSpace -
      8;

    const maxScroll = this.getMaxVerticalScroll();
    const contentHeight = this.getTotalContentHeight();
    const viewportHeight = this.canvas.offsetHeight - this.rulerHeight;
    const thumbHeight = Math.max(
      this.theme.scrollbar.minThumbSize,
      (viewportHeight / contentHeight) * scrollbarHeight
    );
    const scrollRatio =
      maxScroll > 0 ? this.verticalScrollOffset / maxScroll : 0;
    const thumbY = scrollbarY + (scrollbarHeight - thumbHeight) * scrollRatio;

    return {
      scrollbarX,
      scrollbarY,
      scrollbarWidth,
      scrollbarHeight,
      thumbY,
      thumbHeight,
      maxScroll,
    };
  }

  getHorizontalScrollbarGeometry() {
    const scrollbarHeight = this.theme.scrollbar.width; // Using width as height (thickness)
    // Just above the ruler, in the track viewport's own bottom padding — not
    // measured from the canvas bottom, which put it inside the ruler's own
    // band and let it steal clicks meant for region creation/editing there.
    const rulerY = this.canvas.offsetHeight - this.rulerHeight;
    const scrollbarY = rulerY - scrollbarHeight - 4;
    const scrollbarX = 4;

    // Account for vertical scrollbar space on the right
    const verticalScrollbarSpace = this.shouldShowVerticalScrollbar()
      ? this.theme.scrollbar.width + 8
      : 0;
    const scrollbarWidth = this.canvas.offsetWidth - 8 - verticalScrollbarSpace;

    // Calculate thumb size and position based on timeline viewport
    const totalDuration = this.timeline.getEffectiveDuration(
      this.canvas.offsetWidth
    );
    const visibleDuration = this.timeline.getVisibleDuration();
    const thumbWidth = Math.max(
      this.theme.scrollbar.minThumbSize,
      (visibleDuration / totalDuration) * scrollbarWidth
    );

    const maxScroll = this.getMaxHorizontalScroll();
    const scrollRatio =
      maxScroll > 0 ? this.timeline.viewportScrollTime / maxScroll : 0;
    const thumbX = scrollbarX + (scrollbarWidth - thumbWidth) * scrollRatio;

    return {
      scrollbarX,
      scrollbarY,
      scrollbarWidth,
      scrollbarHeight,
      thumbX,
      thumbWidth,
      maxScroll,
    };
  }

  isPointOnVerticalScrollbarThumb(x, y) {
    if (!this.shouldShowVerticalScrollbar()) return false;
    const geom = this.getVerticalScrollbarGeometry();

    return (
      x >= geom.scrollbarX &&
      x <= geom.scrollbarX + geom.scrollbarWidth &&
      y >= geom.thumbY &&
      y <= geom.thumbY + geom.thumbHeight
    );
  }

  isPointOnVerticalScrollbarTrack(x, y) {
    if (!this.shouldShowVerticalScrollbar()) return false;
    const geom = this.getVerticalScrollbarGeometry();

    const viewportHeight = this.canvas.offsetHeight - this.rulerHeight;
    return (
      x >= geom.scrollbarX &&
      x <= geom.scrollbarX + geom.scrollbarWidth &&
      y >= 0 &&
      y <= viewportHeight
    );
  }

  isPointOnHorizontalScrollbarThumb(x, y) {
    if (!this.shouldShowHorizontalScrollbar()) return false;
    const geom = this.getHorizontalScrollbarGeometry();

    return (
      x >= geom.thumbX &&
      x <= geom.thumbX + geom.thumbWidth &&
      y >= geom.scrollbarY &&
      y <= geom.scrollbarY + geom.scrollbarHeight
    );
  }

  isPointOnHorizontalScrollbarTrack(x, y) {
    if (!this.shouldShowHorizontalScrollbar()) return false;
    const geom = this.getHorizontalScrollbarGeometry();

    return (
      x >= geom.scrollbarX &&
      x <= geom.scrollbarX + geom.scrollbarWidth &&
      y >= geom.scrollbarY &&
      y <= geom.scrollbarY + geom.scrollbarHeight
    );
  }

  drawDefaultScrollbar(geom, isActive, orientation) {
    // Draw track background
    this.ctx.fillStyle = this.theme.scrollbar.trackColor;
    this.ctx.fillRect(
      geom.scrollbarX,
      geom.scrollbarY,
      geom.scrollbarWidth,
      geom.scrollbarHeight
    );

    // Draw thumb
    this.ctx.fillStyle = isActive
      ? this.theme.scrollbar.thumbColorActive
      : this.theme.scrollbar.thumbColor;
    if (orientation === "vertical") {
      this.ctx.fillRect(
        geom.scrollbarX,
        geom.thumbY,
        geom.scrollbarWidth,
        geom.thumbHeight
      );
    } else {
      this.ctx.fillRect(
        geom.thumbX,
        geom.scrollbarY,
        geom.thumbWidth,
        geom.scrollbarHeight
      );
    }

    // Draw thumb border
    this.ctx.strokeStyle = this.theme.scrollbar.thumbBorder;
    this.ctx.lineWidth = 1;
    if (orientation === "vertical") {
      this.ctx.strokeRect(
        geom.scrollbarX,
        geom.thumbY,
        geom.scrollbarWidth,
        geom.thumbHeight
      );
    } else {
      this.ctx.strokeRect(
        geom.thumbX,
        geom.scrollbarY,
        geom.thumbWidth,
        geom.scrollbarHeight
      );
    }
  }

  drawVerticalScrollbar() {
    if (!this.shouldShowVerticalScrollbar()) return;

    const geom = this.getVerticalScrollbarGeometry();
    const isActive = this.scrollbarDragState !== null;

    if (this.theme.scrollbar.customDrawFunction) {
      this.theme.scrollbar.customDrawFunction(
        this.ctx,
        geom,
        isActive,
        this.theme,
        "vertical"
      );
    } else {
      this.drawDefaultScrollbar(geom, isActive, "vertical");
    }
  }

  drawHorizontalScrollbar() {
    if (!this.shouldShowHorizontalScrollbar()) return;

    const geom = this.getHorizontalScrollbarGeometry();
    const isActive = this.horizontalScrollbarDragState !== null;

    if (this.theme.scrollbar.customDrawFunction) {
      this.theme.scrollbar.customDrawFunction(
        this.ctx,
        geom,
        isActive,
        this.theme,
        "horizontal"
      );
    } else {
      this.drawDefaultScrollbar(geom, isActive, "horizontal");
    }
  }

  /**
   * Whether a group's name is drawn on its regions: the group's own `showName`
   * if it set one, otherwise the DAW-wide `regionGroupNameDisplay` default.
   */
  isRegionGroupNameShown(groupId, group) {
    if (!group.name) return false;
    if (group.showName === true || group.showName === false) return group.showName;
    if (this.regionGroupNameDisplay === "always") return true;
    if (this.regionGroupNameDisplay === "active") {
      return groupId === this.timeline.activeRegionGroupId;
    }
    return false;
  }

  drawSelection() {
    const width = this.canvas.offsetWidth;
    const height = this.canvas.offsetHeight;
    const rulerY = height - this.rulerHeight;
    const activeId = this.timeline.activeRegionGroupId;

    for (const [groupId, group] of this.timeline.getVisibleRegionGroupEntries()) {
      const isActive = groupId === activeId;
      const style = {
        color: group.color || this.theme.selectionBorder,
        textColor: group.textColor || null,
        active: isActive,
        name: this.isRegionGroupNameShown(groupId, group) ? group.name : null,
      };

      // A 6px bar has no room for a name, so minimized groups never get one.
      if (!isActive && this.regionDisplayMode === "minimized") {
        for (const region of group.regions) {
          this.drawRegionBar(region, style.color);
        }
        continue;
      }

      for (let i = 0; i < group.regions.length; i++) {
        // Show labels if: hovering, dragging this region, or creating
        const showLabels =
          isActive &&
          (this.hoveredRegionIndex === i ||
            (this.regionDragState && this.regionDragState.regionIndex === i));
        this.drawRegion(group.regions[i], width, height, rulerY, showLabels, style);
      }
    }

    // Draw temporary selection (while dragging to create new region) — always
    // in the active group's colours, drawn in full regardless of display mode.
    if (this.timeline.selection) {
      const activeGroup = this.getActiveGroup();
      this.drawRegion(this.timeline.selection, width, height, rulerY, true, {
        color: (activeGroup && activeGroup.color) || this.theme.selectionBorder,
        textColor: (activeGroup && activeGroup.textColor) || null,
        active: true,
        name: null,
      });
    }
  }

  /**
   * One region, full treatment: translucent fill, solid border, and — only
   * for the active group — handles and (on request) time labels. A non-active
   * group is dimmed by `inactiveRegionGroupOpacity` as a single wash over
   * fill+border together, and never gets handles, so it reads as something to
   * look at rather than something to drag.
   *
   * @param {{color: string, textColor: string|null, active: boolean, name: string|null}} style
   */
  drawRegion(region, width, height, rulerY, showLabels, style) {
    const { color, active } = style;
    const startX = this.timeline.timeToCanvasX(region.start);
    const endX = this.timeline.timeToCanvasX(region.end);
    const dim = active ? 1 : this.theme.inactiveRegionGroupOpacity;
    const handleWidth = this.theme.selectionHandleWidth;
    const handleHeight = 30; // Fixed 30px height
    const handleY = rulerY + 5; // 5px margin from top of ruler (leaves 25px at bottom)

    this.ctx.save();

    this.ctx.fillStyle = color;
    this.ctx.globalAlpha = this.theme.selectionFillOpacity * dim;
    this.ctx.fillRect(startX, 0, endX - startX, height);

    this.ctx.globalAlpha = dim;
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = this.theme.selectionBorderWidth;
    this.ctx.beginPath();
    this.ctx.moveTo(startX, 0);
    this.ctx.lineTo(startX, height);
    this.ctx.moveTo(endX, 0);
    this.ctx.lineTo(endX, height);
    this.ctx.stroke();

    this.ctx.globalAlpha = 1;

    if (active) {
      // Draw handles in ruler area
      this.ctx.fillStyle = color;
      this.ctx.fillRect(startX - handleWidth / 2, handleY, handleWidth, handleHeight);
      this.ctx.fillRect(endX - handleWidth / 2, handleY, handleWidth, handleHeight);
    }

    // The name sits level with the handles, between them.
    if (style.name) {
      this.drawRegionGroupName(
        style.name,
        startX,
        endX,
        width,
        handleY + handleHeight / 2,
        handleWidth,
        style.textColor || this.theme.regionGroupNameColor
      );
    }

    // Draw time labels when hovering, dragging, or creating
    if (active && showLabels) {
      const textColor = style.textColor || this.theme.regionLabelColor;
      this.ctx.font = this.theme.regionLabelFont;

      const startLabel = region.start.toFixed(3) + "s";
      const endLabel = region.end.toFixed(3) + "s";
      const durationLabel = (region.end - region.start).toFixed(3) + "s";

      // Labels are centred, so sit each clear of its edge by half its measured
      // width plus a gap (which also clears the chip padding).
      const gap = 9;
      const startWidth = this.ctx.measureText(startLabel).width;
      const endWidth = this.ctx.measureText(endLabel).width;

      this.drawTextChip(startLabel, startX - startWidth / 2 - gap, rulerY + 21, textColor);
      this.drawTextChip(endLabel, endX + endWidth / 2 + gap, rulerY + 21, textColor);
      this.drawTextChip(durationLabel, (startX + endX) / 2, rulerY - 10, textColor);
    }

    this.ctx.restore();
  }

  /**
   * Centred text on a small backing chip (`theme.regionLabelBackground`), so
   * it stays legible over any region colour and over the dark ruler alike.
   * Uses whatever font is already set on the context.
   */
  drawTextChip(text, cx, cy, color) {
    const ctx = this.ctx;
    const textWidth = ctx.measureText(text).width;
    const chipHeight = fontSizePx(ctx.font) + 4;
    const pad = 3;

    const background = this.theme.regionLabelBackground;
    if (background && background !== "transparent") {
      ctx.fillStyle = background;
      ctx.fillRect(cx - textWidth / 2 - pad, cy - chipHeight / 2, textWidth + pad * 2, chipHeight);
    }

    ctx.fillStyle = color;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, cx, cy);
  }

  /**
   * A group's name on one of its regions, centred on whatever part of the
   * region is actually on screen — so it stays readable however far zoomed in
   * you are, even when the region runs off both edges — and inside the
   * handles. Letters drop off the end as room runs out, down to nothing.
   */
  drawRegionGroupName(name, startX, endX, width, cy, handleWidth, color) {
    const inset = handleWidth / 2 + 4;
    const edge = 4;
    const chipPad = 3;
    const left = Math.max(startX + inset, edge);
    const right = Math.min(endX - inset, width - edge);
    const available = right - left - chipPad * 2;
    if (available <= 0) return;

    this.ctx.font = this.theme.regionGroupNameFont;
    const text = ViewportRenderer.fitText(this.ctx, name, available);
    if (!text) return;

    this.drawTextChip(text, (left + right) / 2, cy, color);
  }

  /**
   * The longest prefix of `text` that fits in `maxWidth` with the current
   * font — the whole thing if it fits, otherwise a prefix with an ellipsis,
   * or "" when not even one letter and the ellipsis fit.
   */
  static fitText(ctx, text, maxWidth) {
    if (ctx.measureText(text).width <= maxWidth) return text;

    const ellipsis = "…";
    let lo = 0;
    let hi = text.length - 1;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      if (ctx.measureText(text.slice(0, mid) + ellipsis).width <= maxWidth) lo = mid;
      else hi = mid - 1;
    }

    const prefix = text.slice(0, lo).trimEnd();
    return prefix ? prefix + ellipsis : "";
  }

  /**
   * "Minimized" display mode's stand-in for a non-active group: a thin
   * colour bar along one edge instead of a full-height box, so several
   * groups can stay visible without their overlays piling up on the lanes.
   * `theme.regionGroupBarPosition` picks which edge.
   */
  drawRegionBar(region, color) {
    const startX = this.timeline.timeToCanvasX(region.start);
    const endX = this.timeline.timeToCanvasX(region.end);

    this.ctx.fillStyle = color;
    this.ctx.fillRect(startX, this.getRegionGroupBarY(), endX - startX, this.theme.regionGroupBarHeight);
  }

  drawPlayhead() {
    const currentTime = this.daw ? this.daw.currentTime : 0;
    const x = this.timeline.timeToCanvasX(currentTime);

    if (x >= 0 && x <= this.canvas.offsetWidth) {
      this.ctx.strokeStyle = this.theme.playheadColor;
      this.ctx.lineWidth = this.theme.playheadWidth;
      this.ctx.beginPath();
      this.ctx.moveTo(x, 0);
      this.ctx.lineTo(x, this.canvas.offsetHeight);
      this.ctx.stroke();
      this.ctx.lineWidth = 1;
    }
  }

  formatTime(seconds) {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      // Format: 1:23:45 or 12:34:56
      return `${hours}:${mins.toString().padStart(2, "0")}:${Math.floor(secs)
        .toString()
        .padStart(2, "0")}`;
    } else if (mins > 0) {
      // Format: 1:23.4 or 12:34.5
      return `${mins}:${secs.toFixed(1).padStart(4, "0")}`;
    } else {
      // Format: 12.3s
      return `${secs.toFixed(1)}s`;
    }
  }

  log(message) {
    if (this.daw && this.daw.log) {
      this.daw.log(message);
    }
  }
}
